import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/app/store';
import { updateActiveTenantId } from '../redux/features/app/appSlice';
import { EmptyGuid } from './shared/Alerts';
import {
  Dropdown,
  makeStyles,
  Option,
  shorthands,
  useId,
  DropdownProps,
  SelectionEvents,
  OptionOnSelectData,
} from "@fluentui/react-components";
import { Constants } from '../Constants';

const useStyles = makeStyles({
  root: {
    // Stack the label above the field with a gap
    display: "grid",
    gridTemplateRows: "repeat(1fr)",
    justifyItems: "start",
    ...shorthands.gap("2px"),
    maxWidth: "400px",
  },
});

export const TenantSelector: React.FC<Partial<DropdownProps>> = (props) => {
  const dropdownId = useId("dropdown-tenant");
  const [selectedTenant, setSelectedTenant] = useState<string>(EmptyGuid);
  const activeUserInfo = useSelector((state: RootState) => state.app.activeUserInfo);
  const dispatch = useDispatch();

  const tenants = React.useMemo(() => activeUserInfo?.tenants ?? [], [activeUserInfo]);


  useEffect(() => {
    const savedTenant = localStorage.getItem(Constants.cacheKeys.LOCAL_CACHE_CURRENT_TENANTID);

    if (savedTenant) {
      setSelectedTenant(savedTenant);
      dispatch(updateActiveTenantId(savedTenant));
    } else if (activeUserInfo?.activeTenantId && activeUserInfo.activeTenantId !== EmptyGuid) {
      setSelectedTenant(activeUserInfo.activeTenantId);
    } else if (tenants.length > 0) {
      const firstTenant = tenants[0].id;
      setSelectedTenant(firstTenant);
      dispatch(updateActiveTenantId(firstTenant));
    }
  }, [activeUserInfo, tenants, dispatch]);

  const handleTenantChange = (_: SelectionEvents, data: OptionOnSelectData) => {
    const newTenant = data.optionValue as string;
    if (newTenant !== selectedTenant) {
      setSelectedTenant(newTenant);
      dispatch(updateActiveTenantId(newTenant));
      localStorage.setItem(Constants.cacheKeys.LOCAL_CACHE_CURRENT_TENANTID, newTenant);
      if (process.env.REACT_APP_ENVIRONMENT !== Constants.environment.PRODUCTION) {
        console.log("selected value from dropdown ", newTenant);
      }
      window.location.reload(); // Reload the app
    }
  };
  const styles = useStyles();

  const getSelectedTenantName = () => {
    const value = tenants.find(tenant => tenant.id === selectedTenant)?.tenantName ?? 'Select Tenant';
    return value;
  };

  return (

    <div className={styles.root}>
      <Dropdown
        aria-labelledby={dropdownId}
        selectedOptions={[selectedTenant]}
        onOptionSelect={handleTenantChange}
        defaultValue={getSelectedTenantName()}
        placeholder={getSelectedTenantName()}
        {...props}
      >
        {tenants.map((tenant) => (
          <Option key={tenant.id} value={tenant.id}>
            {tenant.tenantName}
          </Option>
        ))}
      </Dropdown>
    </div>
  );
};

export default TenantSelector;